import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { H3, Subheading } from 'components/base/Text';
import Icon from 'components/base/Icon';

import Queries from 'graphql/Queries';

const ProgressWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
`;

const Milestone = styled.div`
  display: inline-flex;
  margin-top: -0.625rem;

  &:first-of-type {
    margin-top: 0;
  }
  &:nth-of-type(2) {
    svg {
      animation-delay: 100ms;
    }
  }
  &:nth-of-type(3) {
    svg {
      animation-delay: 200ms;
    }
  }
  &:nth-of-type(4) {
    svg {
      animation-delay: 300ms;
    }
  }

  h3 {
    color: var(--purple-gray);
    margin: 0.1em 0 0;
    ${props => (props.complete || props.inProgress) && css`
      color: var(--purpleS);
      margin: 0;
    `}
  }
`;

const MilestoneLine = styled.div`
  align-items: flex-start;
  background-color: var(--purple-grayT);
  display: flex;
  height: 3.5rem;
  margin: 1.6875rem 0 0 -1.25rem;
  transition: 400ms background-color ease;
  width: 0.5rem;
  ${props => props.complete && css`
    background-color: var(--green);
  `}

  ${Milestone}:last-of-type & {
    background: transparent;
    height: 0;
    margin-right: 0.475rem;
    width: 0;
  }

  @media (min-width: 50em) {
    margin: 2.125rem 0 0 -1.5rem;
  }
`;

const completeMilestoneIcon = keyframes`
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

const completeMilestoneIconSvg = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const MilestoneIcon = styled.div`
  border: 0.25rem solid transparent;
  border-radius: 50%;
  display: inline-block;
  height: 2rem;
  width: 2rem;

  div {
    background-color: var(--purpleTT);
    border: 0.25rem solid var(--purple-grayT);
    border-radius: 50%;
    color: var(--purpleTT);
    display: inline-flex;
  }
  svg {
    display: flex;
    height: 1rem;
    width: 1rem;
  }

  ${props => props.complete && css`
    div {
      background-color: var(--green);
      border-width: 0.3125rem;
    }
    svg {
      animation: ${completeMilestoneIconSvg} 400ms ease forwards;
      color: var(--white);
      height: 0.875rem;
      opacity: 0;
      width: 0.875rem;
    }
  `}
  ${props => props.inProgress && css`
    div {
      background-color: var(--greenTTT);
      color: var(--greenTTT);
    }
  `}
  ${props => (props.complete || props.inProgress) && css`
    animation: ${completeMilestoneIcon} 400ms ease forwards;
    border-color: rgba(var(--green-rgb), 0.15);
    opacity: 0;
    div {
      border-color: var(--green);
    }
  `}

  @media (min-width: 50em) {
    border-width: 0.3125rem;
    height: 2.5rem;
    width: 2.5rem;
    svg {
      height: 1.4rem;
      width: 1.4rem;
    }
    
    ${props => props.complete && css`
      div {
        border-width: 0.4375rem;
      }
      svg {
        height: 1rem;
        width: 1rem;
      }
    `}
  }
`;

const MilestoneInfo = styled.div`
  margin-left: 2rem;

  div {
    display: block;
  }
  h3 {
    font-size: var(--step-down-1);
  }
  h6 {
    font-size: var(--step-down-2);
    margin: 0;
  }
`;

const milestoneNames = ['Loading Milestones', 'Hit the Docs', 'Sign These Documents', 'Project Kickoff Meeting'];

const ProgressLoading = () => (
  <React.Fragment>
    {milestoneNames.map(idx => {
      return (
        <Milestone
          key={idx}
          complete={false}
          inProgress={false}
        >
          <MilestoneIcon
            complete={false}
            inProgress={false}
          >
            <div>
              <Icon id="check-circle" title="Check Circle" />
            </div>
          </MilestoneIcon>
          <MilestoneLine
            complete={false}
            inProgress={false}
          />
          <MilestoneInfo>
            <div>
              <H3>{idx}</H3>
              <Subheading>Loading...</Subheading>
            </div>
          </MilestoneInfo>
        </Milestone>
      )
    })}
  </React.Fragment>
);

const Progress = ({ show, projectId }) => (
  <ProgressWrapper show={show}>
    <Queries.Project variables={{ id: projectId }}>
      {(project, { loading }) => {
        if (loading) return <ProgressLoading />;

        return (
          project.milestones.map((milestone, idx) => (
            <Milestone
              key={idx}
              complete={milestone.isComplete}
              inProgress={milestone.isInProgress}
            >
              <MilestoneIcon
                complete={milestone.isComplete}
                inProgress={milestone.isInProgress}
              >
                <div>
                  <Icon id="check-circle" title="Check Circle" />
                </div>
              </MilestoneIcon>
              <MilestoneLine
                complete={milestone.isComplete}
                inProgress={milestone.isInProgress}
              />
              <MilestoneInfo>
                <div>
                  <H3>{milestone.name}</H3>
                  <Subheading>
                    {milestone.isComplete && 'Complete'}
                    {milestone.isInProgress && 'In Progress'}
                    {!milestone.isComplete &&
                      !milestone.isInProgress &&
                      'Up Next'}
                  </Subheading>
                </div>
              </MilestoneInfo>
            </Milestone>
          ))
        );
      }}
    </Queries.Project>
  </ProgressWrapper>
);

export default Progress;



// WEBPACK FOOTER //
// ./src/layouts/ProjectLayout/Progress.js